import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useContext, useState, useEffect } from 'react';
import { ApiContext, WizardContext } from '../../../utils/contexts.js';
import ExtendedStart from './ExtendedStart.js';
import ExtendedSteps from './ExtendedSteps.js';
import { Box, Text, Spinner } from '@hausgold/designsystem';
import analyticsTrack from '../../../utils/tracking.js';
import { jsx } from '@emotion/react';
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
var extendedSteps = ['current_usage', 'ownership', 'evaluation_reason'];

/**
 * Provides the entry to the extended dialog.
 *
 * The extended one is shown after the normal dialog (basic wizard steps) are completed
 * and is used to get optional additional data without the fear that the customer jumps off.
 */
var ExtendedWizard = function ExtendedWizard() {
  var _useContext = useContext(ApiContext),
    redirectUrl = _useContext.redirectUrl;
  var _useContext2 = useContext(WizardContext),
    dialogId = _useContext2.dialogId,
    leadId = _useContext2.leadId,
    recruiterId = _useContext2.recruiterId;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    question = _useState2[0],
    setQuestion = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showSpinner = _useState4[0],
    setShowSpinner = _useState4[1];
  var _useState5 = useState({
      current_usage: undefined,
      ownership: undefined,
      evaluation_reason: undefined
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    stepValues = _useState6[0],
    setStepValues = _useState6[1];

  // Send extra event to track that lead conversion is done now.
  // This is needed as the normal "last_click" event from user_data_step is not fired for extended variant.
  useEffect(function () {
    analyticsTrack({
      event: 'Formwizard Finished',
      params: {
        dialog_id: dialogId,
        lead_id: leadId,
        recruiter_id: recruiterId,
        label: 'conversion_done',
        group: 'formwizard_additional_steps'
      }
    });
  }, []);
  var onSendExtendedTracking = function onSendExtendedTracking(_ref) {
    var value = _ref.value;
    // Send current step or "fallback" if on start page
    analyticsTrack({
      event: 'Formwizard Interacted',
      params: {
        dialog_id: dialogId,
        lead_id: leadId,
        recruiter_id: recruiterId,
        // Value is either a specific value from card or "weiter"
        label: value,
        group: 'formwizard_additional_steps'
      }
    });
  };

  // Ensure user gets feedback if done and redirect to VP finally.
  var handleRedirect = function handleRedirect() {
    var shouldSendTracking = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (shouldSendTracking) {
      onSendExtendedTracking({
        currentStep: question,
        value: 'überspringen'
      });
    }
    setShowSpinner(true);

    // NOTE: Use +window.top.location+ to break out of the
    //       iframe (redirect the whole page/tab).
    window.location.href = redirectUrl;
  };

  // Handle back Button mechanic and go back to previous step or start page.
  var handleGoBack = function handleGoBack() {
    var currentStepPosition = extendedSteps.findIndex(function (step) {
      return step === question;
    });
    var previousStep = currentStepPosition === 0 ? null : extendedSteps[currentStepPosition - 1];
    setQuestion(previousStep);
  };

  // Handle weiter/"value" Button mechanic by saving value if usefully and go to next step or call redirect if the end is reached.
  var handleGoForward = function handleGoForward(value) {
    // Send tracking
    onSendExtendedTracking({
      currentStep: question,
      value: value
    });

    // If no question is set yat we are on start page and want to the first step now.
    if (!question) {
      setQuestion(extendedSteps[0]);
      return;
    }

    // Set new value to current step
    setStepValues(_objectSpread(_objectSpread({}, stepValues), {}, _defineProperty({}, question, value === 'weiter' ? null : value)));

    // get current step and check if a next step exists
    var currentStepPosition = extendedSteps.findIndex(function (step) {
      return step === question;
    });
    var nextStep = currentStepPosition === extendedSteps.length - 1 ? null : extendedSteps[currentStepPosition + 1];

    // If next step exists go on with it.
    if (nextStep) {
      setQuestion(nextStep);
      // Else we are done and call redirect function
    } else {
      handleRedirect(false);
    }
  };
  if (showSpinner) {
    return jsx(Box, {
      textAlign: "center"
    }, jsx(Text, {
      as: "div",
      fontSize: "xl"
    }, "Einen Moment noch. Wir leiten Sie weiter."), jsx(Spinner, null));
  }

  // If no question is selected we show the "start page" where the user can choose between go forward and skip.
  if (!question) {
    return jsx(ExtendedStart, {
      handleRedirect: handleRedirect,
      handleGoForward: handleGoForward
    });
  }

  // If the user choose "go forward" we guide him through the hard-coded questions
  return jsx(ExtendedSteps, {
    extendedSteps: extendedSteps,
    question: question,
    selectedValue: stepValues[question],
    handleGoBack: handleGoBack,
    handleGoForward: handleGoForward
  });
};
export { ExtendedWizard as default };